import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { getAudioBinary } from 'services/getAudioBinary';
import { formatTime } from './helpers';

export default function AudioStepPlayer({ audio, t, objetivoId }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState(false);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    const loadAndPlay = () => {
        if (audioUrl) {
            audioRef.current && audioRef.current.play();
            setIsPlaying(true);
            return;
        }
        if (!audio.hash_md5 || !objetivoId) return;
        setAudioLoading(true);
        setAudioError(false);
        const token = sessionStorage.getItem('key');
        getAudioBinary(objetivoId, token, audio.hash_md5)
            .then(url => {
                setAudioUrl(url);
                setAudioLoading(false);
            })
            .catch(() => {
                setAudioLoading(false);
                setAudioError(true);
            });
    };

    const handlePlayPause = () => {
        if (audioLoading) return;
        if (!audioUrl) {
            loadAndPlay();
            return;
        }
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    const handleProgressClick = (e) => {
        if (!audioRef.current || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = x / rect.width;
        audioRef.current.currentTime = pct * duration;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    const handleCanPlay = () => {
        if (audioRef.current && !isPlaying) {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const stepLabel = audio.paso_nombre
        || (audio.paso_orden != null ? `${t('cqp.audio.step')} ${audio.paso_orden}` : t('cqp.audio.step'));
    const dirLabel = audio.direccion ? ` (${audio.direccion})` : '';

    return (
        <Box style={{
            backgroundColor: '#f8f9fb',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 4,
            border: '1px solid #e8ecf1',
        }}>
            <Box display="flex" alignItems="center" mb={0.5} style={{ minWidth: 0 }}>
                <VolumeUpIcon style={{ fontSize: 16, color: '#1976d2', marginRight: 6, flexShrink: 0 }} />
                <Typography variant="body2" noWrap style={{ fontWeight: 600, color: '#353535', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${stepLabel}${dirLabel}`}>
                    {stepLabel}{dirLabel}
                </Typography>
                {audio.fecha_tz && (
                    <Typography variant="caption" noWrap style={{ color: '#999', marginLeft: 8, fontSize: 11, flexShrink: 0 }}>
                        {audio.fecha_tz.replace('T', ' ')}
                    </Typography>
                )}
            </Box>
            {audioError ? (
                <Typography variant="caption" style={{ color: '#C2272D' }}>
                    {t('cqp.audio.error')}
                </Typography>
            ) : (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <IconButton size="small" onClick={handlePlayPause} disabled={audioLoading} style={{ color: '#1976d2' }}>
                        {audioLoading
                            ? <Typography variant="caption" style={{ fontSize: 10 }}>...</Typography>
                            : isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <Typography variant="caption" style={{ color: '#666', minWidth: 36, textAlign: 'right' }}>
                        {formatTime(currentTime)}
                    </Typography>
                    <Box
                        style={{ flex: 1, height: 6, backgroundColor: '#e0e0e0', borderRadius: 3, cursor: 'pointer', position: 'relative' }}
                        onClick={handleProgressClick}
                    >
                        <Box style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: '#1976d2',
                            borderRadius: 3,
                            transition: 'width 0.1s linear',
                        }} />
                    </Box>
                    <Typography variant="caption" style={{ color: '#666', minWidth: 36 }}>
                        {formatTime(duration)}
                    </Typography>
                </Box>
            )}
            {audioUrl && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    preload="auto"
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                    onCanPlay={handleCanPlay}
                />
            )}
        </Box>
    );
}
